import React, { useEffect, useState } from 'react';
import { Card, CardTitle, Grid, Loader, PageTitle } from '@develos/next-components';
import { useFetch, useInfoMessages, useTranslations } from '@develos/next-library';
import { ActiveStream, CameraStream } from './types';
import { useSelector } from 'react-redux';
import { storeSelectors } from '@/store';
import { useStream } from './hooks/useStream';
import StreamPlayer from './components/StreamPlayer';
import StreamSelector from './components/StreamSelector';
import { streamTimeout } from './constants';

const LiveCameraPreview = () => {
  const { translate } = useTranslations();
  const { get } = useFetch();
  const { success, error } = useInfoMessages();

  const [selectedStream, setSelectedStream] = useState<CameraStream | undefined>(undefined);
  const [activeStream, setActiveStream] = useState<ActiveStream | undefined>(undefined);
  const [streamIsLoading, setStreamIsLoading] = useState<boolean>(false);

  const { getStreamSource } = useStream(selectedStream?.uId);

  const currentMetricLocalization = useSelector(storeSelectors.selectCurrentMetricLocalization);

  const { refetch: startCameraStream, isLoading: startIsPending } = get(`LiveCameraPreview/${selectedStream?.uId}/start`, {
    enabled: false,
    refetchOnMount: false,
  });

  const { refetch: stopCameraStream, isLoading: stopIsPending } = get(`LiveCameraPreview/${selectedStream?.uId}/stop`, {
    enabled: false,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (currentMetricLocalization?.uId) {
      handleStopCameraStream();
      setStreamIsLoading(false);
    }
  }, [currentMetricLocalization?.uId])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (activeStream) {
      timeoutId = setTimeout(() => {
        handleStopCameraStream();
        setStreamIsLoading(false);
      }, streamTimeout)
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [activeStream])

  const handleStartCameraStream = async () => {
    try {
      setStreamIsLoading(true);
      if(selectedStream?.uId == "1753dd33-5c97-4be8-95e6-df07f4333c8e") startCameraStream();
      const source = getStreamSource();
      setActiveStream({
        uId: selectedStream!.uId,
        label: selectedStream!.label,
        source
      })
    } catch {
      // error('Wystąpił problem z połaczeniem')
    }
  }

  const handleStopCameraStream = async () => {
    if (!activeStream) return;

    if(selectedStream?.uId == "1753dd33-5c97-4be8-95e6-df07f4333c8e") stopCameraStream();
    success(`Połącznie z kamerą: ${selectedStream?.label} zostało zatrzymane`);
    setActiveStream(undefined);
  }

  const handleStreamConnectionFailed = async (error:any) => {
    console.error(error)
    error('Słaba jakość połaczenia')
    // setActiveStream(undefined);
    // setStreamIsLoading(false);
  }

  const handleStreamConnectionSuccess = () => {
    success(`Nawiązano połączenie z kamerą: ${selectedStream?.label}`);
    setStreamIsLoading(false);
  }

  return (
    <div>
      <div className='rm-relative rm-h-full rm-w-full'>
        <PageTitle title={translate('Podgląd kamer na żywo')} />
        <Grid columns={12} gap={4}>
          <div className="rm-col-span-12 rm-order-last md:rm-col-span-8 md:rm-order-first">
            <Card className='rm-p-4 rm-min-h-[720px]'>
              <CardTitle title={translate('Widok z kamery')} />
              <>
                <StreamPlayer
                  stream={activeStream}
                  width='100%'
                  height='100%'
                  onError={handleStreamConnectionFailed}
                  onPlayerReady={handleStreamConnectionSuccess}
                />
              </>
            </Card>
          </div>

          <div className='rm-col-span-12 md:rm-col-span-4'>
            <StreamSelector
              metricLocalizationId={currentMetricLocalization?.uId}
              selectedStreamId={selectedStream?.uId}
              activeStreamId={activeStream?.uId}
              streamIsLoading={startIsPending || stopIsPending || streamIsLoading}
              handleStreamSelected={(stream) => setSelectedStream(stream)}
              handleStartStream={handleStartCameraStream}
              handleStopStream={handleStopCameraStream}
            />
          </div>
        </Grid>
      </div>
    </div>
  );
};

export default LiveCameraPreview;
